import { energyAnalysisApiSlice } from '../apiSlice';

export const portfolioAnalysisApiSlice = energyAnalysisApiSlice.injectEndpoints(
  {
    endpoints: (builder) => ({
      getPortfolioAnalysisPower: builder.query({
        query: ({ recalc_account_id }) => {
          return {
            url: `/portfolio_analysis/power/${recalc_account_id}`,
            method: 'GET',
          };
        },
        providesTags: ['PortfolioAnalysis'],
      }),
      getPortfolioAnalysisWater: builder.query({
        query: ({ recalc_account_id }) => {
          return {
            url: `/portfolio_analysis/water/${recalc_account_id}`,
            method: 'GET',
          };
        },
        providesTags: ['PortfolioAnalysis'],
      }),
      getPortfolioAnalysisHeat: builder.query({
        query: ({ recalc_account_id }) => {
          return {
            url: `/portfolio_analysis/heat/${recalc_account_id}`,
            method: 'GET',
          };
        },
        providesTags: ['PortfolioAnalysis'],
      }),
      getPortfolioAnalysisSubscription: builder.query({
        query: ({ recalc_account_id }) => {
          return {
            url: `/subscription/${recalc_account_id}`,
            method: 'GET',
          };
        },
        providesTags: ['PortfolioAnalysisSubscription'],
      }),
      getPortfolioAnalysisSubscriptions: builder.query({
        query: () => {
          return {
            url: `/subscriptions`,
            method: 'GET',
          };
        },
        providesTags: ['PortfolioAnalysisSubscription'],
      }),
      getMonthlyData: builder.query({
        query: ({ recalc_account_id, meter_id, analysisType }) => ({
          url: `/monthly_data/${analysisType}/${recalc_account_id}/${meter_id}`,
          method: 'GET',
        }),
        providesTags: ['MonthlyData'],
      }),

      getBenchmarkChartData: builder.query({
        query: ({ accountId, matrikelKey }) => ({
          url: `/detailed_benchmark/${accountId}/${matrikelKey}`,
          method: 'GET',
        }),
        providesTags: ['BenchmarkChartData'],
      }),

      updateBenchmarkOverwrite: builder.mutation({
        query: ({ account_id, matrikel_key, body }) => ({
          url: `/matrikel_overwrite/${account_id}/${matrikel_key}`,
          method: 'PATCH',
          body,
        }),
        invalidatesTags: [
          'BenchmarkOverwrite',
          'PortfolioAnalysisSubscription',
        ],
      }),

      getBuildingUseList: builder.query({
        query: () => ({
          url: `/building_use_list`,
          method: 'GET',
        }),
        providesTags: ['BuildingUseList'],
      }),

      getHeatInstallationList: builder.query({
        query: () => ({
          url: `/heat_installation_list`,
          method: 'GET',
        }),
        providesTags: ['HeatInstallationList'],
      }),
      postPortfolioUpdateNow: builder.mutation({
        query: ({ recalc_account_id }) => ({
          url: `/update_portfolio_analysis_now/${recalc_account_id}`,
          method: 'PUT',
        }),
      }),
      setDaytimeInterval: builder.mutation({
        query: ({
          account_id,
          metering_point_id,
          occupancy,
          analysis_type,
        }) => {
          if (occupancy === null) {
            return {
              url: `/manual_occupancy/${analysis_type}/${account_id}/${metering_point_id}`,
              method: 'PUT',
            };
          }

          return {
            url: `/manual_occupancy/${analysis_type}/${account_id}/${metering_point_id}`,
            method: 'PUT',
            body: { ...occupancy },
          };
        },
        invalidatesTags: ['PortfolioAnalysis', 'PortfolioAnalysisSubscription'],
      }),
      getPortfolioHourAnalysis: builder.query({
        query: (args) => {
          const {
            accountId,
            meterId,
            start,
            end,
            activeDaysFilter,
            activeHolidaysFilter,
            analysisType,
          } = args;

          const params = new URLSearchParams({
            start_timestamp: new Date(start).toISOString(), // Ensure ISO format
            end_timestamp: new Date(end).toISOString(), // Ensure ISO format
          });

          if (activeDaysFilter !== undefined) {
            params.append('active_days_filter', activeDaysFilter.toString());
          }
          if (activeHolidaysFilter !== undefined) {
            params.append('holidays_filter', activeHolidaysFilter.toString());
          }

          return {
            url: `/hour_analysis_portfolio/${analysisType}/${accountId}/${meterId}?${params.toString()}`,
            method: 'GET',
          };
        },
        providesTags: ['PortfolioAnalysis'],
      }),
      getPortfolioDurationAnalysis: builder.query({
        query: (args) => {
          const {
            analysisType,
            accountId,
            meterId,
            start,
            end,
            activeHoursFilter, // Optional boolean
            activeDaysFilter, // Optional boolean
            activeHolidaysFilter, // Optional boolean
          } = args;

          const params = new URLSearchParams({
            start_timestamp: new Date(start).toISOString(), // Ensure ISO format
            end_timestamp: new Date(end).toISOString(), // Ensure ISO format
          });

          // Conditionally append parameters only if they are explicitly set (true or false)
          if (activeHoursFilter !== undefined) {
            params.append('active_hours_filter', activeHoursFilter.toString());
          }
          if (activeDaysFilter !== undefined) {
            params.append('active_days_filter', activeDaysFilter.toString());
          }
          if (activeHolidaysFilter !== undefined) {
            params.append('holidays_filter', activeHolidaysFilter.toString());
          }

          return {
            url: `/duration_analysis_portfolio/${analysisType}/${accountId}/${meterId}?${params.toString()}`,
            method: 'GET',
          };
        },
        providesTags: ['PortfolioAnalysis'],
      }),
      getPortfolioConsumptionData: builder.query({
        query: ({ accountId, meterId, start, end, analysisType }) => {
          const params = new URLSearchParams({
            start_timestamp: new Date(start).toISOString(), // Ensure ISO format
            end_timestamp: new Date(end).toISOString(), // Ensure ISO format
          });

          return {
            url: `/consumption_data/${analysisType}/${accountId}/${meterId}?${params.toString()}`,
            method: 'GET',
          };
        },
        providesTags: ['PortfolioAnalysis'],
      }),
      updatePortfolioAnalysisSubscription: builder.mutation({
        query: ({ recalc_account_id, body }) => ({
          url: `/subscription/${recalc_account_id}`,
          method: 'PATCH',
          body,
        }),
        invalidatesTags: ['PortfolioAnalysisSubscription'],
      }),
      postPortfolioAnalysisSubscription: builder.mutation({
        query: (body) => ({
          url: `/subscription`,
          method: 'POST',
          body,
        }),
        invalidatesTags: ['PortfolioAnalysisSubscription'],
      }),
      getPortfolioPowerMeters: builder.query({
        query: ({ recalc_account_id }) => ({
          url: `/portfolio_meters/power/${recalc_account_id}`,
          method: 'GET',
        }),
        providesTags: ['PortfolioAnalysis'],
      }),
      getPortfolioWaterMeters: builder.query({
        query: ({ recalc_account_id }) => ({
          url: `/portfolio_meters/water/${recalc_account_id}`,
          method: 'GET',
        }),
        providesTags: ['PortfolioAnalysis'],
      }),
      getPortfolioHeatMeters: builder.query({
        query: ({ recalc_account_id }) => ({
          url: `/portfolio_meters/heat/${recalc_account_id}`,
          method: 'GET',
        }),
        providesTags: ['PortfolioAnalysis'],
      }),
      getOccupancy: builder.query({
        query: ({ recalc_account_id, meter_id, analysis_type }) => ({
          url: `/occupancy/${analysis_type}/${recalc_account_id}/${meter_id}`,
          method: 'GET',
        }),
        providesTags: ['PortfolioAnalysis'],
      }),
      getMatrikelAnalysis: builder.query({
        query: ({ recalc_account_id }) => ({
          url: `/matrikel_analysis/${recalc_account_id}`,
          method: 'GET',
        }),
        providesTags: ['PortfolioAnalysis'],
      }),
      getMatrikelMetadata: builder.query({
        query: ({ recalc_account_id }) => ({
          url: `/matrikel_metadata/${recalc_account_id}`,
          method: 'GET',
        }),
        providesTags: ['PortfolioAnalysis'],
      }),
      getMatrikelMetadataById: builder.query({
        query: ({ recalc_account_id, matrikel_key }) => ({
          url: `/matrikel_metadata/${recalc_account_id}/${matrikel_key}`,
          method: 'GET',
        }),
        providesTags: ['PortfolioAnalysis', 'BenchmarkOverwrite'],
      }),
      putMeterDisplayName: builder.mutation({
        query: ({
          recalc_account_id,
          meter_id,
          analysisType,
          displayName,
        }) => ({
          url: `/meter_display_name/${analysisType}/${recalc_account_id}/${meter_id}?display_name=${displayName}`,
          method: 'PUT',
        }),
        invalidatesTags: ['PortfolioAnalysis'],
      }),
    }),
  },
);

export const {
  useGetPortfolioAnalysisPowerQuery,
  useGetPortfolioAnalysisWaterQuery,
  useGetPortfolioAnalysisHeatQuery,
  useGetPortfolioAnalysisSubscriptionQuery,
  useGetPortfolioAnalysisSubscriptionsQuery,
  useGetMonthlyDataQuery,
  useGetBenchmarkChartDataQuery,
  useUpdateBenchmarkOverwriteMutation,
  useGetBuildingUseListQuery,
  useGetHeatInstallationListQuery,
  usePostPortfolioUpdateNowMutation,
  useSetDaytimeIntervalMutation,
  useGetPortfolioHourAnalysisQuery,
  useGetPortfolioDurationAnalysisQuery,
  useGetPortfolioConsumptionDataQuery,
  useUpdatePortfolioAnalysisSubscriptionMutation,
  usePostPortfolioAnalysisSubscriptionMutation,
  useGetPortfolioHeatMetersQuery,
  useGetPortfolioPowerMetersQuery,
  useGetPortfolioWaterMetersQuery,
  useGetMatrikelAnalysisQuery,
  useGetOccupancyQuery,
  useGetMatrikelMetadataQuery,
  useGetMatrikelMetadataByIdQuery,
  usePutMeterDisplayNameMutation,
} = portfolioAnalysisApiSlice;
